import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { debugLog, debugWarn, debugError } from '../../common/utils/debug';
import { useParams, useNavigate } from 'react-router-dom';
import { CircularProgress } from '@mui/material';
import { useAppState } from '../../hooks/useAppState';
import { useConnection } from '../../hooks/useConnection';
import { useEventListeners } from '../../hooks/useEventListeners';
import { useMessageHandling } from '../../hooks/useMessageHandling';
import { usePerformanceOptimizations } from '../../hooks/usePerformanceOptimizations';
import { useStreamingTranscription } from '../../hooks/useStreamingTranscription';
import { signalRService } from '../../services/signalr.service';
import { interviewService } from '../../services/interview.service';
import { isStopCommand } from '../../common/utils/voice-command.utils';
import { AudioControlsProps } from '../../types';
import AppHeader from '../../components/layout/AppHeader';
import AvatarFooter from '../../components/avatar_new/AvatarFooter';
import Avatar from '../../components/avatar_new/Avatar';
import Chat from '../../components/chat/Chat';
import InterviewWelcomeDialog from '../../components/dialogs/InterviewWelcomeDialog';
import DeviceCheckDialog from '../../components/dialogs/DeviceCheckDialog';
import QuitInterviewDialog from '../../components/dialogs/QuitInterviewDialog';
import InterviewInterruptedDialog from '../../components/dialogs/InterviewInterruptedDialog';
import InterviewCompletedDialog from '../../components/dialogs/InterviewCompletedDialog';
import { useMute } from '../../context/mute';
import { usePause } from '../../context/pause';
import { azureCustomAvatarService } from '../../services/azure-custom-avatar.service';
import { ThinkingProcessStatus, InterruptedIcon } from '../../components/chat/ThinkingProcess';

type SessionPageState =
  | 'loading'
  | 'welcome'
  | 'device-check'
  | 'interview'
  | 'error';

interface SessionDetails {
  candidateName: string;
}

const SessionPage: React.FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();

  const [pageState, setPageState] = useState<SessionPageState>('loading');
  const [sessionDetails, setSessionDetails] = useState<SessionDetails | null>(null);
  const [connectionLost, setConnectionLost] = useState(false);
  const [showQuitDialog, setShowQuitDialog] = useState(false);
  const [showInterruptedDialog, setShowInterruptedDialog] = useState(false);
  // TODO: Future work - Backend will send a flag via SignalR (e.g., isInterviewComplete: true in RESPONSE_TEXT event)
  // to trigger the InterviewCompletedDialog. Listen for this in session-connection-event-handlers.ts
  const [showCompletedDialog, setShowCompletedDialog] = useState(false);
  const [awaitingGreeting, setAwaitingGreeting] = useState(false); // Track if waiting for greeting to start speaking
  const greetingRequestSent = useRef(false); // Track if greeting has been requested

  // Apply performance optimizations
  usePerformanceOptimizations();

  const {
    messages,
    sessionState,
    audioState,
    isThinking,
    addMessage,
    updateLastMessage,
    setSessionState,
    setAudioState,
    addToolCall,
    updateToolCall,
    resetSession,
    clearMessages,
    setIsThinking,
  } = useAppState();

  const { handleConnect, handleDisconnect } = useConnection({
    addMessage,
    updateLastMessage,
    setSessionState,
    setAudioState,
    addToolCall,
    updateToolCall,
    resetSession,
    sessionId: sessionId!,
    setIsThinking,
  });

  const { isMuted, setMuted, forceUnmute } = useMute();
  const { setPaused } = usePause();

  // Compute thinking process status
  const thinkingStatus: ThinkingProcessStatus = useMemo(() => {
    // Error state - device check failed or error page state
    if (connectionLost || pageState === 'error') {
      return 'error';
    }

    // Interrupted state - quit dialog or interrupted dialog is showing
    if (showQuitDialog || showInterruptedDialog) {
      return 'interrupted';
    }

    // Completed state - interview successfully completed
    if (showCompletedDialog) {
      return 'completed';
    }

    // Pending state - loading, welcome, device-check, or avatar not ready
    if (
      pageState === 'loading' ||
      pageState === 'welcome' ||
      pageState === 'device-check' ||
      !sessionState.avatarReady ||
      !sessionState.isConnected
    ) {
      return 'pending';
    }

    // Pending state - avatar is speaking OR awaiting greeting to start
    if (audioState.isSpeaking || awaitingGreeting) {
      return 'pending';
    }

    // Ready state - avatar connected and muted
    if (isMuted) {
      return 'ready';
    }

    // Listening state - avatar connected, unmuted, and not speaking
    return 'listening';
  }, [
    connectionLost,
    pageState,
    showQuitDialog,
    showInterruptedDialog,
    showCompletedDialog,
    sessionState.avatarReady,
    sessionState.isConnected,
    isMuted,
    audioState.isSpeaking,
    awaitingGreeting,
  ]);

  // Ref to hold clearTranscription to avoid circular dependency
  const clearTranscriptionRef = useRef<(() => void) | null>(null);

  const { handleSendMessage } = useMessageHandling({
    addMessage,
    setMuted,
    clearTranscription: () => clearTranscriptionRef.current?.(),
  });

  useEventListeners({
    addMessage,
    setSessionState,
    handleDisconnect,
  });

  const { interimText, finalText, isStreaming, clearTranscription } = useStreamingTranscription({
    onFinalTranscription: (text) => {
      debugLog("🎤 [SessionPage] onFinalTranscription callback triggered with:", text);
      debugLog("🎤 [SessionPage] Timestamp:", Date.now());
      if (text.trim()) {
        const trimmedText = text.trim();

        if (isStopCommand(trimmedText)) {
          signalRService.stopProcessing();
          return;
        }

        debugLog("🎤 [SessionPage] Calling handleSendMessage with:", trimmedText);
        handleSendMessage(trimmedText);
      }
    },
    audioState, // Pass audioState to enable avatar cancellation
  });

  // Set the ref after useStreamingTranscription is called
  clearTranscriptionRef.current = clearTranscription;

  // Clear awaitingGreeting when avatar starts speaking (greeting has started)
  useEffect(() => {
    if (awaitingGreeting && audioState.isSpeaking) {
      debugLog('🎭 [SessionPage] Avatar started speaking - clearing awaitingGreeting flag');
      setAwaitingGreeting(false);
    }
  }, [awaitingGreeting, audioState.isSpeaking]);

  // Clear chat on app start
  useEffect(() => {
    clearMessages();
  }, [clearMessages]);

  // Listen for interview completion event
  useEffect(() => {
    const handleInterviewCompletedEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      debugLog('🏁 [SessionPage] Interview completed event received:', customEvent.detail);
      
      // Check completion reason to determine which dialog to show
      const completionReason = customEvent.detail?.completionReason || 'normal';
      
      // Close quit dialog if it's still open
      setShowQuitDialog(false);
      
      if (completionReason === 'timeout') {
        // Show interrupt dialog for timeout completions
        debugLog('⏱️ [SessionPage] Showing interrupt dialog (timeout)');
        setShowInterruptedDialog(true);
      } else if (completionReason === 'exited') {
        // Show interrupt dialog for user-initiated exit
        debugLog('🚪 [SessionPage] Showing interrupt dialog (exited by user)');
        setShowInterruptedDialog(true);
      } else {
        // Show completed dialog for normal completions
        debugLog('✅ [SessionPage] Showing completed dialog (normal)');
        setShowCompletedDialog(true);
      }
    };

    window.addEventListener('interview_completed', handleInterviewCompletedEvent);
    
    return () => {
      window.removeEventListener('interview_completed', handleInterviewCompletedEvent);
    };
  }, []);

  // Validate session on mount - calls backend validation API
  useEffect(() => {
    const validateInterview = async () => {
      if (!sessionId) {
        navigate('/not-found');
        return;
      }

      try {
        debugLog('[SessionPage] Validating interview link via backend API:', sessionId);
        const result = await interviewService.verifyInterview(sessionId);

        if (!result.isValid) {
          debugWarn('[SessionPage] Interview validation failed:', result);
          // Redirect to link-expired page for any validation failure
          navigate('/link-expired');
          return;
        }

        debugLog('[SessionPage] Interview validated successfully:', result);
        
        // Set candidate name from validation result for consent form
        if (result.candidateName) {
          setSessionDetails({
            candidateName: result.candidateName,
          });
        }

        setPageState('welcome');
      } catch (error) {
        debugError('[SessionPage] Interview validation error:', error);
        navigate('/link-expired');
      }
    };

    validateInterview();
  }, [sessionId, navigate]);

  // Handle welcome dialog start - connect avatar (muted) while device checks run
  const handleWelcomeStart = useCallback(() => {
    debugLog('[SessionPage] handleWelcomeStart: Starting device check and connecting avatar (muted)...');
    setPageState('device-check');
    // Start connecting the avatar in the background (it will be muted by default)
    handleConnect();
  }, [handleConnect]);

  // Handle all device checks passed - unmute the avatar
  const handleAllChecksPassed = useCallback(async () => {
    // Prevent duplicate calls from React Strict Mode
    if (greetingRequestSent.current) {
      debugLog('⏭️ [SessionPage] Greeting already requested, skipping duplicate');
      return;
    }
    
    debugLog('✅ [SessionPage] handleAllChecksPassed: All device checks passed!');
    setPageState('interview');
    debugLog('📄 [SessionPage] Page state set to interview');
    // DO NOT unmute yet - microphone will auto-unmute after processExplanation finishes
    debugLog('🔇 [SessionPage] Keeping microphone muted until processExplanation completes');
    
    // Set awaiting greeting flag to keep UI in pending state until greeting starts speaking
    setAwaitingGreeting(true);
    debugLog('⏳ [SessionPage] Set awaitingGreeting=true to keep UI in pending state');
    
    // Wait for avatar to be ready before sending greeting
    debugLog('⏳ [SessionPage] Checking if avatar is ready (sessionState.avatarReady:', sessionState.avatarReady, ')');
    
    const waitForAvatarReady = new Promise<void>((resolve) => {
      if (sessionState.avatarReady) {
        debugLog('✅ [SessionPage] Avatar already ready, sending greeting immediately');
        resolve();
      } else {
        debugLog('⏳ [SessionPage] Waiting for avatar to be ready before sending greeting...');
        const handleAvatarReady = () => {
          debugLog('🎭 [SessionPage] Avatar ready event received, will send greeting');
          window.removeEventListener('avatar_ready', handleAvatarReady);
          resolve();
        };
        window.addEventListener('avatar_ready', handleAvatarReady);
      }
    });
    
    await waitForAvatarReady;
    
    // Mark as sent BEFORE the async operations to prevent race conditions
    greetingRequestSent.current = true;
    
    // Brief pause to let user see the avatar before she starts speaking
    debugLog('⏸️ [SessionPage] Pause before greeting (letting user see avatar)...');
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    debugLog('📤 [SessionPage] Requesting greeting from backend...');
    await signalRService.sendGreeting();
    debugLog('✅ [SessionPage] Greeting request sent');
  }, [setMuted, sessionState.avatarReady]);

  // Handle device checks failed
  const handleChecksFailed = useCallback(() => {
    debugLog('[SessionPage] handleChecksFailed: Checks failed, disconnecting avatar...');
    // Disconnect the avatar
    handleDisconnect();
    // Set connection lost state
    setConnectionLost(true);
  }, [handleDisconnect]);

  // Handle quit interview button click
  const handleQuitClick = useCallback(async () => {
    // Pause the avatar and mute the mic
    const { speechToTextSileroService } = await import(
      '../../services/speech-to-text-silero.service'
    );
    speechToTextSileroService.stopRecording();
    await azureCustomAvatarService.pauseSpeaking();
    setPaused(true);
    setMuted(true);
    setShowQuitDialog(true);
  }, [setPaused, setMuted]);

  // Handle continue interview (close quit dialog)
  const handleContinueInterview = useCallback(async () => {
    // Unpause the avatar and unmute the mic
    await azureCustomAvatarService.resumeSpeaking();
    const { speechToTextSileroService } = await import(
      '../../services/speech-to-text-silero.service'
    );
    await speechToTextSileroService.startRecording();
    setPaused(false);
    // Use forceUnmute to clear manual mute flag - user deliberately chose to continue
    forceUnmute();
    setShowQuitDialog(false);
  }, [setPaused, forceUnmute]);

  // Handle quit anyway (exit interview via backend)
  const handleQuitAnyway = useCallback(async () => {
    setShowQuitDialog(false);
    
    try {
      debugLog('🚪 [SessionPage] Exiting interview via backend...');
      // Call backend to mark interview as interrupted and generate report
      await signalRService.exitInterview();
      debugLog('✅ [SessionPage] Exit interview request sent successfully');
      // The backend will send interview_completed event which will show the appropriate dialog
    } catch (error) {
      debugError('❌ [SessionPage] Error exiting interview:', error);
      // Fallback: disconnect and show interrupted dialog
      handleDisconnect();
      setShowInterruptedDialog(true);
    }
  }, [handleDisconnect]);

  const avatarControls: AudioControlsProps = useMemo(
    () => ({
      isConnected: sessionState.isConnected,
      avatarReady: sessionState.avatarReady,
      onConnect: handleConnect,
      onDisconnect: handleDisconnect,
      isConnecting: sessionState.isConnecting,
      isDisconnecting: sessionState.isDisconnecting,
      isSpeaking: audioState.isSpeaking,
    }),
    [
      sessionState.isConnected,
      sessionState.avatarReady,
      sessionState.isConnecting,
      sessionState.isDisconnecting,
      audioState.isSpeaking,
      handleConnect,
      handleDisconnect,
    ]
  );

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen bg-primary-navy-100 flex items-center justify-center">
        <div className="text-center">
          <CircularProgress size={60} className="!text-primary-blue-100" />
          <p className="text-white mt-4">Validating your session...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Welcome Dialog */}
      <InterviewWelcomeDialog
        open={pageState === 'welcome'}
        onStart={handleWelcomeStart}
        candidateName={sessionDetails?.candidateName}
      />

      {/* Device Check Dialog */}
      <DeviceCheckDialog
        open={pageState === 'device-check'}
        onAllChecksPassed={handleAllChecksPassed}
        onChecksFailed={handleChecksFailed}
      />

      {/* Quit Interview Dialog */}
      <QuitInterviewDialog
        open={showQuitDialog}
        onContinue={handleContinueInterview}
        onQuit={handleQuitAnyway}
      />

      {/* Interview Interrupted Dialog */}
      <InterviewInterruptedDialog open={showInterruptedDialog} />

      {/* Interview Completed Dialog */}
      {/* TODO: showCompletedDialog will be triggered by SignalR event from backend */}
      <InterviewCompletedDialog open={showCompletedDialog} />

      {/* Main Interview UI - only show when in interview state */}
      {pageState === 'interview' && (
        <div className="flex flex-col md:flex-row h-screen p-6 gap-6 bg-black">
          <main className="h-[25%] md:h-full w-full md:w-[70%] relative rounded-xl overflow-hidden">
            <div className="bg-primary-navy-100 h-full">
              <Avatar
                isConnected={sessionState.isConnected}
                isSpeaking={audioState.isSpeaking}
                avatarReady={sessionState.avatarReady}
                onConnect={handleConnect}
                controls={avatarControls}
                connectionLost={connectionLost}
              />
            </div>

            <div className="absolute top-0 left-0 right-0">
              <AppHeader onQuitClick={handleQuitClick} />
            </div>

            <div className="absolute bottom-0 left-0 right-0">
              <AvatarFooter controls={avatarControls} />
            </div>
          </main>

          <section className="h-[75%] md:h-full w-full md:w-[30%] md:min-w-[400px] flex flex-col bg-primary-navy-100 text-primary-white-100 rounded-xl overflow-hidden">
            <Chat
              messages={messages}
              onSendMessage={handleSendMessage}
              avatarReady={sessionState.avatarReady}
              isConnected={sessionState.isConnected}
              sessionId={sessionState.sessionId}
              streamingTranscription={{
                interimText,
                finalText,
                isStreaming,
              }}
              thinkingStatus={thinkingStatus}
              isSpeaking={audioState.isSpeaking}
              isThinking={isThinking}
            />
          </section>
        </div>
      )}
    </>
  );
};

export default SessionPage;
